import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';
import logger from '../../utils/logger';

const MovementForm = ({ stockItems, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    movementType: 'OUT',
    transactionType: 'ALLOCATION',
    date: new Date().toISOString().split('T')[0],
    jobcardNo: '',
    vehicleRegNo: '',
    vehicleModel: '',
    customerName: '',
    technician: '',
    supplier: '',
    remarks: '',
  });

  // Multiple items for a single job card
  const [items, setItems] = useState([
    { id: 1, stockItemId: '', quantity: 0 }
  ]);

  const [errors, setErrors] = useState({});
  const [jobCards, setJobCards] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [showTechnicianManager, setShowTechnicianManager] = useState(false);
  const [newTechnicianName, setNewTechnicianName] = useState('');

  // Fetch job cards
  useEffect(() => {
    const fetchJobCards = async () => {
      try {
        const q = query(
          collection(db, 'jobs'),
          where('status', '!=', 'Closed')
        );
        const querySnapshot = await getDocs(q);
        const jobs = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setJobCards(jobs);
      } catch (error) {
        logger.error('Error fetching job cards:', error);
      }
    };

    fetchJobCards();
  }, []);

  // Fetch technicians from dedicated collection
  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        // First try to get from dedicated technicians collection
        const techSnapshot = await getDocs(collection(db, 'technicians'));
        if (!techSnapshot.empty) {
          const techs = techSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setTechnicians(techs);
        } else {
          // Fallback to employees with role Mechanic/Technician
          const empSnapshot = await getDocs(collection(db, 'employees'));
          const emps = empSnapshot.docs
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
            .filter(emp =>
              emp.role === 'Mechanic' ||
              emp.role === 'Technician' ||
              emp.department === 'Workshop'
            );
          setTechnicians(emps.map(emp => ({
            id: emp.id,
            name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
            status: emp.status || 'active'
          })));
        }
      } catch (error) {
        logger.error('Error fetching technicians:', error);
      }
    };

    fetchTechnicians();
  }, []);

  // Auto-populate fields when job card is selected
  const handleJobCardChange = (jobCardId) => {
    const job = jobCards.find(j => j.id === jobCardId);
    if (job) {
      setFormData(prev => ({
        ...prev,
        jobcardNo: job.jobCardNo || '',
        vehicleRegNo: job.registrationNumber || '',
        vehicleModel: `${job.vehicleMake || ''} ${job.vehicleModel || ''}`.trim(),
        customerName: job.customerName || '',
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'jobcardSelect') {
      handleJobCardChange(value);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle item changes
  const handleItemChange = (itemId, field, value) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        return { ...item, [field]: value };
      }
      return item;
    }));

    // Clear item errors
    if (errors[`item_${itemId}_${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`item_${itemId}_${field}`];
        return newErrors;
      });
    }
  };

  // Add new item row
  const addItemRow = () => {
    const newId = Math.max(...items.map(i => i.id)) + 1;
    setItems([...items, { id: newId, stockItemId: '', quantity: 0 }]);
  };

  // Remove item row
  const removeItemRow = (itemId) => {
    if (items.length === 1) {
      toast.warning('At least one item is required');
      return;
    }
    setItems(items.filter(item => item.id !== itemId));
  };

  // Get stock item details
  const getStockItem = (stockItemId) => {
    return stockItems.find(s => s.id === stockItemId);
  };

  // Update transaction type options based on movement type
  const getTransactionTypes = () => {
    if (formData.movementType === 'IN') {
      return [
        { value: 'PURCHASE', label: 'Purchase' },
        { value: 'RETURN', label: 'Return from Job' },
        { value: 'ADJUSTMENT', label: 'Adjustment (Add)' },
      ];
    } else {
      return [
        { value: 'ALLOCATION', label: 'Allocation to Job' },
        { value: 'OFFICE_USE', label: 'Office/Consumable Use' },
        { value: 'ADJUSTMENT', label: 'Adjustment (Remove)' },
        { value: 'DAMAGE', label: 'Damage/Loss' },
      ];
    }
  };

  // Add new technician
  const handleAddTechnician = async () => {
    if (!newTechnicianName.trim()) {
      toast.error('Please enter a technician name');
      return;
    }

    try {
      const docRef = await addDoc(collection(db, 'technicians'), {
        name: newTechnicianName.trim(),
        status: 'active',
        createdAt: new Date(),
      });

      setTechnicians([...technicians, {
        id: docRef.id,
        name: newTechnicianName.trim(),
        status: 'active'
      }]);

      setNewTechnicianName('');
      toast.success(`Technician "${newTechnicianName.trim()}" added successfully`);
    } catch (error) {
      logger.error('Error adding technician:', error);
      toast.error('Failed to add technician');
    }
  };

  // Remove technician (mark as inactive)
  const handleRemoveTechnician = async (techId, techName) => {
    if (!window.confirm(`Are you sure you want to remove "${techName}" from the technicians list?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'technicians', techId));
      setTechnicians(technicians.filter(t => t.id !== techId));
      toast.success(`Technician "${techName}" removed successfully`);
    } catch (error) {
      logger.error('Error removing technician:', error);
      toast.error('Failed to remove technician');
    }
  };

  const validate = () => {
    const newErrors = {};

    // Validate each item
    items.forEach((item, index) => {
      if (!item.stockItemId) {
        newErrors[`item_${item.id}_stockItemId`] = 'Please select a stock item';
      }

      if (!item.quantity || item.quantity <= 0) {
        newErrors[`item_${item.id}_quantity`] = 'Quantity must be greater than 0';
      }

      if (formData.movementType === 'OUT' && item.stockItemId) {
        const stockItem = getStockItem(item.stockItemId);
        if (stockItem && item.quantity > stockItem.quantity) {
          newErrors[`item_${item.id}_quantity`] = `Cannot exceed available stock (${stockItem.quantity})`;
        }
      }
    });

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    // Conditional validations based on transaction type
    if (formData.transactionType === 'ALLOCATION' && !formData.jobcardNo) {
      newErrors.jobcardNo = 'Job card is required for allocations';
    }

    if (formData.transactionType === 'PURCHASE' && !formData.supplier) {
      newErrors.supplier = 'Supplier is required for purchases';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Submit data for each item
    const submitData = {
      ...formData,
      date: new Date(formData.date),
      items: items.map(item => ({
        stockItemId: item.stockItemId,
        quantity: parseFloat(item.quantity),
      })),
    };

    onSubmit(submitData);
  };

  // Calculate total value for all items
  const calculateTotalValue = () => {
    return items.reduce((total, item) => {
      const stockItem = getStockItem(item.stockItemId);
      if (stockItem && item.quantity > 0) {
        return total + (stockItem.unitCost * item.quantity);
      }
      return total;
    }, 0);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {/* Movement Type */}
        <div>
          <label htmlFor="movementType" className="block text-sm font-semibold text-gray-700 mb-2">
            Movement Type <span className="text-red-500">*</span>
          </label>
          <select
            name="movementType"
            id="movementType"
            value={formData.movementType}
            onChange={(e) => {
              handleChange(e);
              setFormData(prev => ({
                ...prev,
                transactionType: e.target.value === 'IN' ? 'PURCHASE' : 'ALLOCATION'
              }));
            }}
            className="mt-1 block w-full rounded-xl border-gray-300 shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
          >
            <option value="IN">IN (Receiving Stock)</option>
            <option value="OUT">OUT (Issuing Stock)</option>
          </select>
        </div>

        {/* Transaction Type */}
        <div>
          <label htmlFor="transactionType" className="block text-sm font-semibold text-gray-700 mb-2">
            Transaction Type <span className="text-red-500">*</span>
          </label>
          <select
            name="transactionType"
            id="transactionType"
            value={formData.transactionType}
            onChange={handleChange}
            className="mt-1 block w-full rounded-xl border-gray-300 shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
          >
            {getTransactionTypes().map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date */}
        <div>
          <label htmlFor="date" className="block text-sm font-semibold text-gray-700 mb-2">
            Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="date"
            id="date"
            value={formData.date}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-xl shadow-sm sm:text-sm ${
              errors.date
                ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500'
            }`}
          />
          {errors.date && <p className="mt-2 text-sm font-medium text-red-600 flex items-center">
            <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {errors.date}
          </p>}
        </div>

        {/* Conditional Fields Based on Transaction Type */}
        {formData.transactionType === 'ALLOCATION' && (
          <>
            {/* Job Card */}
            <div className="sm:col-span-2">
              <label htmlFor="jobcardSelect" className="block text-sm font-medium text-gray-700">
                Job Card <span className="text-red-500">*</span>
              </label>
              <select
                name="jobcardSelect"
                id="jobcardSelect"
                value={formData.jobcardNo}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  errors.jobcardNo
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
              >
                <option value="">Select a job card...</option>
                {jobCards.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.jobCardNo} - {job.customerName} ({job.registrationNumber})
                  </option>
                ))}
              </select>
              {errors.jobcardNo && <p className="mt-1 text-sm text-red-600">{errors.jobcardNo}</p>}
            </div>

            {/* Vehicle & Customer Info (auto-populated) */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Vehicle Registration</label>
              <input
                type="text"
                value={formData.vehicleRegNo}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Vehicle Model</label>
              <input
                type="text"
                value={formData.vehicleModel}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Customer Name</label>
              <input
                type="text"
                value={formData.customerName}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm"
              />
            </div>

            {/* Technician with Management */}
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="technician" className="block text-sm font-medium text-gray-700">
                  Technician
                </label>
                <button
                  type="button"
                  onClick={() => setShowTechnicianManager(!showTechnicianManager)}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {showTechnicianManager ? 'Hide Manager' : 'Manage List'}
                </button>
              </div>
              <select
                name="technician"
                id="technician"
                value={formData.technician}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">Select technician...</option>
                {technicians.filter(t => t.status !== 'inactive').map((tech) => (
                  <option key={tech.id} value={tech.name}>
                    {tech.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Technician Manager Panel */}
        {showTechnicianManager && formData.transactionType === 'ALLOCATION' && (
          <div className="sm:col-span-2 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">Manage Technicians</h4>

            {/* Add New Technician */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newTechnicianName}
                onChange={(e) => setNewTechnicianName(e.target.value)}
                placeholder="Enter technician name"
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              <button
                type="button"
                onClick={handleAddTechnician}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
              >
                Add
              </button>
            </div>

            {/* List of Technicians */}
            <div className="max-h-40 overflow-y-auto space-y-2">
              {technicians.length === 0 ? (
                <p className="text-sm text-gray-500">No technicians found. Add one above.</p>
              ) : (
                technicians.map((tech) => (
                  <div key={tech.id} className="flex items-center justify-between bg-white p-2 rounded border">
                    <span className={`text-sm ${tech.status === 'inactive' ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                      {tech.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTechnician(tech.id, tech.name)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {formData.transactionType === 'PURCHASE' && (
          <>
            {/* Supplier */}
            <div className="sm:col-span-2">
              <label htmlFor="supplier" className="block text-sm font-medium text-gray-700">
                Supplier <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="supplier"
                id="supplier"
                value={formData.supplier}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  errors.supplier
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="Supplier name"
              />
              {errors.supplier && <p className="mt-1 text-sm text-red-600">{errors.supplier}</p>}
            </div>
          </>
        )}
      </div>

      {/* Multiple Stock Items Section */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-gray-900">Stock Items</h4>
          <button
            type="button"
            onClick={addItemRow}
            className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg className="-ml-1 mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Item
          </button>
        </div>

        <div className="space-y-4">
          {items.map((item, index) => {
            const selectedStock = getStockItem(item.stockItemId);
            return (
              <div key={item.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600">Item #{index + 1}</span>
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItemRow(item.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {/* Stock Item Selection */}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Stock Item <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={item.stockItemId}
                      onChange={(e) => handleItemChange(item.id, 'stockItemId', e.target.value)}
                      className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                        errors[`item_${item.id}_stockItemId`]
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                    >
                      <option value="">Select a stock item...</option>
                      {stockItems.map((stock) => (
                        <option key={stock.id} value={stock.id}>
                          {stock.itemName} - {stock.partNumber} (Available: {stock.quantity})
                        </option>
                      ))}
                    </select>
                    {errors[`item_${item.id}_stockItemId`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`item_${item.id}_stockItemId`]}</p>
                    )}
                    {selectedStock && (
                      <p className="mt-1 text-xs text-gray-500">
                        Unit Cost: ₦{selectedStock.unitCost?.toLocaleString('en-NG')}
                      </p>
                    )}
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Quantity <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.1"
                      className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                        errors[`item_${item.id}_quantity`]
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                    />
                    {errors[`item_${item.id}_quantity`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`item_${item.id}_quantity`]}</p>
                    )}
                  </div>
                </div>

                {/* Item Summary */}
                {selectedStock && item.quantity > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between text-sm">
                    <span className="text-gray-600">
                      {formData.movementType === 'IN' ? 'New Balance' : 'Balance After'}:
                      <span className="font-medium ml-1">
                        {formData.movementType === 'IN'
                          ? selectedStock.quantity + item.quantity
                          : selectedStock.quantity - item.quantity
                        }
                      </span>
                    </span>
                    <span className="text-gray-600">
                      Value:
                      <span className="font-medium ml-1">
                        ₦{(item.quantity * selectedStock.unitCost).toLocaleString('en-NG')}
                      </span>
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Remarks */}
      <div>
        <label htmlFor="remarks" className="block text-sm font-medium text-gray-700">
          Remarks / Notes
        </label>
        <textarea
          name="remarks"
          id="remarks"
          rows={3}
          value={formData.remarks}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          placeholder="Additional information about this transaction..."
        />
      </div>

      {/* Transaction Summary */}
      {items.some(item => item.stockItemId && item.quantity > 0) && (
        <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2">Transaction Summary</h4>
          <div className="space-y-1 text-sm text-blue-800">
            <div className="flex justify-between">
              <span>Total Items:</span>
              <span className="font-medium">{items.filter(i => i.stockItemId).length}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Quantity:</span>
              <span className="font-medium">
                {items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0)}
              </span>
            </div>
            <div className="flex justify-between border-t border-blue-300 pt-1 mt-1">
              <span>Total Value:</span>
              <span className="font-bold">₦{calculateTotalValue().toLocaleString('en-NG')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          Record Movement{items.length > 1 ? 's' : ''}
        </button>
      </div>
    </form>
  );
};

export default MovementForm;
